import { z } from "zod";

export type BrandedType<
    T extends Record<"analytics", "user/anonymous" | "user" | "site">
> = `ari:cloud:growth-unified-profile::${T["analytics"]}/${string}`;

const entityTypeSchema = z.enum([
    "AJS_ANONYMOUS_USER",
    "ATLASSIAN_ACCOUNT",
    "SITE",
    "ORG"
]);

export type EntityType = z.infer<typeof entityTypeSchema>;

export type AJS_ANONYMOUS_USER_ID =
    `${(typeof ID_PREFIX)["AJS_ANONYMOUS_USER"]}${string}`;
export type ATLASSIAN_ACCOUNT_ID =
    `${(typeof ID_PREFIX)["ATLASSIAN_ACCOUNT"]}${string}`;
export type SITE_ID = `${(typeof ID_PREFIX)["SITE"]}${string}`;
export type ORG_ID = `${(typeof ID_PREFIX)["ORG"]}${string}`;

export const ID_PREFIX = {
    AJS_ANONYMOUS_USER: "ari:cloud:analytics::user/anonymous/",
    ATLASSIAN_ACCOUNT: "ari:cloud:identity::user/",
    SITE: "ari:cloud:platform::site/",
    ORG: "ari:cloud:platform::org/"
} as const;

const unifiedProfileSupportedIdSchema = z.string().refine(
    (value) => {
        return (
            value.startsWith(ID_PREFIX.AJS_ANONYMOUS_USER) ||
            value.startsWith(ID_PREFIX.ATLASSIAN_ACCOUNT) ||
            value.startsWith(ID_PREFIX.SITE) ||
            value.startsWith(ID_PREFIX.ORG)
        );
    },
    {
        message: "unifiedProfileSupportedIds must start with a valid ID prefix."
    }
);

export type UnifiedProfileSupportedIds = z.infer<
    typeof unifiedProfileSupportedIdSchema
>;

const channelSchema = z.enum([
    "PAID_CONTENT",
    "PAID_DISPLAY",
    "PAID_REVIEW_SITES",
    "PAID_SEARCH",
    "PAID_SOCIAL"
]);

export type Channel = z.infer<typeof channelSchema>;

const utmSchema = z.object({
    keyword: z.string().optional(),
    source: z.string().optional(),
    channel: channelSchema.optional()
});

export type Utm = z.infer<typeof utmSchema>;

const anchorTypeSchema = z.enum(["SEO", "PFM"]);

const anchorSchema = z.object({
    name: z.string(),
    type: anchorTypeSchema
});

export type Anchor = z.infer<typeof anchorSchema>;

const teamTypeSchema = z.enum([
    "DATA_SCIENCE",
    "PRODUCT_MANAGEMENT",
    "PROGRAM_MANAGEMENT",
    "PROJECT_MANAGEMENT",
    "SOFTWARE_DEVELOPMENT",
    "SOFTWARE_ENGINEERING",
    "MARKETING",
    "IT_SUPPORT",
    "DESIGN",
    "OPERATIONS",
    "SALES",
    "HUMAN_RESOURCES",
    "LEGAL",
    "FINANCE",
    "CUSTOMER_SERVICE",
    "OTHER"
]);

export type TeamType = z.infer<typeof teamTypeSchema>;

const paidChannelContext = z.object({
    utm: utmSchema,
    anchor: anchorSchema,
    subAnchor: z.string(),
    teamType: teamTypeSchema,
    persona: z.string(),
    templates: z.array(z.string())
});

const productSchema = z.enum([
    "jira",
    "confluence",
    "jsm", // Jira service management
    "jpd",
    "jwm", // Jira work management
    "trello",
    "compass"
]);

export type GUPSProductKeys = z.infer<typeof productSchema>;

const paidChannelContextByProduct = z.record(
    productSchema,
    paidChannelContext.partial()
);

export type PaidChannelContextByProduct = z.infer<
    typeof paidChannelContextByProduct
>;

const seoContext = z.object({ anchor: anchorSchema });
export type SeoContext = z.infer<typeof seoContext>;

const jtbdSchema = z.enum([
    "AD_HOC_TASK_AND_INCIDENT_MANAGEMENT",
    "BUDGETS",
    "CD_WRTNG",
    "CENTRALIZED_DOCUMENTATION",
    "COMPLIANCE_AND_RISK_MANAGEMENT",
    "IMPROVE_WORKFLOW",
    "LAUNCH_CAMPAIGNS",
    "MANAGING_CLIENT_AND_VENDOR_RELATIONSHIPS",
    "MANAGE_TASKS",
    "MAP_WORK_DEPENDENCIES",
    "MARKETING_CONTENT",
    "PLAN_AND_MANAGE",
    "PRIORITIZE_WORK",
    "PROJECT_PLANNING",
    "PROJECT_PLANNING_AND_COORDINATION",
    "PROJECT_PROGRESS",
    "STAKEHOLDERS",
    "STRATEGIES_AND_GOALS",
    "SYSTEM_AND_TOOL_EVALUATIONS",
    "TRACKING_RPRTNG",
    "TRACK_BUGS",
    "USE_KANBAN_BOARD",
    "WORK_IN_SCRUM",
    "RUN_SPRINTS"
]);

export type JTBD = z.infer<typeof jtbdSchema>;

export const JiraFamiliarityObject = {
    NEW: 0,
    MIDDLE: 1,
    EXPERIENCE: 2
} as const;

const jiraFamiliarityEnum = z.enum(["EXPERIENCE", "MIDDLE", "NEW"]);

export type JiraFamiliarity = z.infer<typeof jiraFamiliarityEnum>;

const issueTypeSchema = z.object({
    avatarId: z.string(),
    name: z.string()
});

const projectLandingSelectionEnum = z.enum([
    "SAMPLE_PROJECT",
    "CREATE_PROJECT"
]);
export type ProjectLandingSelection = z.infer<
    typeof projectLandingSelectionEnum
>;

const jiraOnboardingContextSchema = z.object({
    projectName: z.string(),
    teamType: teamTypeSchema,
    jobsToBeDone: z.array(jtbdSchema),
    jiraFamiliarity: jiraFamiliarityEnum,
    experienceLevel: z.string(),
    template: z.string(),
    persona: z.string(),
    issueTypes: z.array(issueTypeSchema),
    statusNames: z.array(z.string()),
    projectLandingSelection: projectLandingSelectionEnum
});

const confluencingOnboardingContextSchema = z.object({
    jobsToBeDone: z.array(jtbdSchema),
    template: z.string()
});

const onboardingContextSchema = z.object({
    jira: jiraOnboardingContextSchema.partial(),
    confluence: confluencingOnboardingContextSchema.partial()
});

const companyTypeSchema = z.enum(["PUBLIC", "PRIVATE"]);
const companySizeSchema = z.enum(["SMALL", "MEDIUM", "LARGE", "UNKNOWN"]);
const enterpriseAccountStatusSchema = z.enum([
    "GAM",
    "PLATINUM",
    "GOLD",
    "SILVER",
    "BRONZE"
]);
const enrichmentStatusSchema = z.enum([
    "PENDING",
    "IN_PROGRESS",
    "COMPLETE",
    "ERROR"
]);

export type EnrichmentStatus = z.infer<typeof enrichmentStatusSchema>;

const companySchema = z.object({
    businessName: z.string(),
    domain: z.string(),
    annualRevenue: z.number(),
    revenueCurrency: z.string(),
    enterpriseSized: z.boolean(),
    employeeStrength: z.number(),
    type: companyTypeSchema,
    size: companySizeSchema,
    accountStatus: enterpriseAccountStatusSchema,
    marketCap: z.string(),
    sector: z.string(),
    description: z.string()
});

const linkedEntitySchema = z.object({
    linkedId: z.string(),
    entityType: entityTypeSchema,
    createdAt: z.string()
});

export type LinkedEntity = z.infer<typeof linkedEntitySchema>;

const productDetailsSchema = z.object({
    productName: z.string(),
    productKey: z.string(),
    productEdition: z.string().optional(),
    d0Eligible: z.boolean().optional(),
    d1to6Eligible: z.boolean().optional(),
    d0Active: z.boolean().optional(),
    d1to6Active: z.boolean().optional(),
    provisionedAt: z.string().optional()
});

export type ProductDetails = z.infer<typeof productDetailsSchema>;

const siteDetailsSchema = z.object({
    cloudId: z.string(),
    displayName: z.string().optional(),
    url: z.string().optional(),
    hasAdminAccess: z.boolean().optional(),
    siteCreatedAt: z.string().optional(),
    products: z.array(productDetailsSchema).optional()
});

export type SiteDetails = z.infer<typeof siteDetailsSchema>;

const userFootprintsSchema = z.object({
    hasAtlassianAccount: z.boolean(),
    products: z.array(productSchema)
});

export type UserFootprints = z.infer<typeof userFootprintsSchema>;

export const growthUnifiedProfileSchema = z
    .object({
        entityType: entityTypeSchema,
        paidChannelContext: paidChannelContextByProduct.optional(),
        seoContext: seoContext.partial().optional(),
        onboardingContext: onboardingContextSchema.partial().optional(),
        company: companySchema.partial().optional(),
        enrichmentStatus: enrichmentStatusSchema,
        linkedEntities: z.array(linkedEntitySchema).optional(),
        sites: z.array(siteDetailsSchema).optional(),
        userFootprints: userFootprintsSchema.optional()
    })
    .strict();

export const growthUnifiedProfileInput = z.object({
    onboardingContext: onboardingContextSchema.partial(),
    paidChannelContext: paidChannelContextByProduct.optional()
});

export type GrowthUnifiedProfileInput = z.infer<
    typeof growthUnifiedProfileInput
>;

export const createUnifiedProfileInput = z
    .object({
        accountId: z.string().optional(),
        anonymousId: z.string().optional(),
        tenantId: z.string().optional(),
        unifiedProfile: growthUnifiedProfileInput.partial()
    })
    .strict();

export type CreateUnifiedProfileInput = z.infer<
    typeof createUnifiedProfileInput
>;

export const getUnifiedProfileInput = z
    .object({
        accountId: z.string().optional(),
        anonymousId: z.string().optional(),
        tenantId: z.string().optional()
    })
    .strict();

export type GetUnifiedProfileInput = z.infer<typeof getUnifiedProfileInput>;

export type GrowthUnifiedProfile = z.infer<typeof growthUnifiedProfileSchema>;

export type TeamTypeOptions = z.infer<typeof teamTypeSchema>;

export type JTBDOptions = z.infer<typeof jtbdSchema>;
